import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorService } from './http-error.service';
import { ErrorNotification } from './error-notification';
import { ProfileService } from '../auth/profile.service';
import { Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'admin-error',
    templateUrl: './http-error.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule]
})
export class HttpErrorComponent implements OnInit, OnDestroy {
  private readonly translationIndex = 'error';
  status: number;
  message: string;
  errorCode: string;
  guid: string;
  
  private subscription$: Subscription = new Subscription();
  private subscriptionReset$: Subscription = new Subscription();

  constructor(private readonly errntf: HttpErrorService, private readonly ref: ChangeDetectorRef, private readonly pfs: ProfileService) { }

  private getMessageForBaseError(translation: any, error: HttpErrorResponse): string {
    const message = translation[error.status];
    if (message) {
      return message;
    }
    // Deliberately not error.statusText (Sonar S1874): Angular deprecated it because with HTTP/2
    // and later it stays 'OK' even when the status is not 200, so a status with no translation
    // entry would show the user the word "OK" as the error message.
    //
    // The status number stays in the message though. Only 0/400/401/403/404/500 have a translation,
    // so anything else — 409, 429, 502, 503 — lands here, and the banner renders nothing but this
    // string plus a correlation id that gateway-generated errors do not carry. A bare "Unknown
    // error" would leave both the user and support with nothing to go on.
    //
    // One key with a {0} placeholder rather than two fragments concatenated here: word order and
    // punctuation around the status are the translator's to decide, not this file's. Present in all
    // 7 locales. (`unknownError` / `failedWithStatus` stay in the locale files untouched — they
    // predate this by years and are not ours to delete from a resource adm2 may also read.)
    return this.pfs.getFormattedMessage(translation['unknownErrorWithStatus'], [error.status]);
  }

  private getMessageForCustomError(translation: any, error: HttpErrorResponse): string {
    const err = error.error;
    let msg = translation[err.odpErrorCode + '_' + err.odpExceptionName];
    if (msg) {
      if (err.params) {
        const keys = Object.keys(err.params);
        const value = err.params[keys[0]];
        if (Array.isArray(value)) {
          const str = value.join(', ');
          msg = this.pfs.getFormattedMessage(msg, [str]);
        } else {
          msg = this.pfs.getFormattedMessage(msg, [value]);
        }
      }
    } else {
      msg = err.detailedMessage;
    }
    return msg;
  }

  private reset(): void {
    this.status = 0;
    this.ref.detectChanges();
  }

  ngOnInit() {
    const translation = this.pfs.messages[this.translationIndex];
    this.ref.detach();
    this.subscription$ = this.errntf.notification$
      .subscribe(ntf => {
        if (ntf?.type === ErrorNotification.TYPE.http) {
          this.guid = ntf.error.headers.get('x-correlation-id');
          this.status = ntf.error.status;
          this.errorCode = translation['errorCode'];
          if (ntf.error.error?.odpErrorCode) {
            this.message = this.getMessageForCustomError(translation, ntf.error);
          } else {
            this.message = this.getMessageForBaseError(translation, ntf.error);
          }
        }
        this.ref.detectChanges();
      });

    this.subscriptionReset$ = this.errntf.reset$
      .subscribe(value => {
        if (value) {
          this.reset();
        }
      });
  }

  ngOnDestroy() {
    this.subscription$.unsubscribe();
    this.subscriptionReset$.unsubscribe();
  }

  close() {
    this.reset();
  }

}
